import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    Divider,
    IconButton,
    Stack,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ClientDashboardLayout from '../ClientDashboardLayout';
import { getCategoryBreakdown } from '../../../api/expenseBreakdown';
import { fetchCustomerAccounts } from '../../../api/accounts';
import { getMetricData } from '../../../api/metric';
import { createCustomerScenario, getCustomerScenario, listCustomerScenarios, updateCustomerScenario } from '../../../api/scenarios';

const moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// Layout
const PANEL_MIN_HEIGHT_LG = 720;
const PANEL_HEIGHT_LG = 'calc(100vh - 260px)';

const SCENARIO_NAME_MAX_WORDS = 8;
const SCENARIO_DESCRIPTION_MAX_WORDS = 20;

const RESPONSIVE = {
    pageTitleFontSize: { xs: 24, xl: 28 },
    pageTitleLineHeight: { xs: 1.334, xl: 1.25 },
    primaryButtonFontSize: { xs: 14, xl: 15 },
    primaryButtonPaddingX: { xs: '16px', xl: '18px' },
    primaryButtonPaddingY: { xs: '6px', xl: '8px' },
    headerPaddingX: { xs: 2, sm: 3, xl: 4 },
    panelInnerPaddingX: { xs: '16px', xl: '20px' },
    metricValueFontSize: { xs: 15, xl: 16 },
    chipFontSize: { xs: 12, xl: 13 },
};

const limitToWords = (value, maxWords) => {
    const str = String(value ?? '');
    const trimmed = str.trim();
    if (!trimmed) return str;
    const words = trimmed.split(/\s+/);
    if (words.length <= maxWords) return str;
    return words.slice(0, maxWords).join(' ');
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    if (!Number.isFinite(v)) return '$0';
    return moneyFmt.format(v);
};

const formatPercent1 = (n) => {
    const v = Number(n || 0);
    if (!Number.isFinite(v)) return '0.0%';
    return `${v.toFixed(1)}%`;
};

const toDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const toAccountNumber = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return null;
    const num = Number(digits);
    return Number.isFinite(num) ? num : null;
};

const inAnyRange = (accountNumber, ranges) => {
    if (!Number.isFinite(accountNumber)) return false;
    return (ranges || []).some(([start, end]) => accountNumber >= start && accountNumber <= end);
};

const clamp0 = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return 0;
    return Math.max(0, v);
};

const ADJUST_STEP = 500;

const buildDefaultScenarioName = (num, endDateStr) => {
    const safeNum = Number.isFinite(Number(num)) ? Number(num) : 1;
    return `Scenario ${safeNum} (${endDateStr})`;
};

const isKeyboardToggleEvent = (e) => e?.key === 'Enter' || e?.key === ' ';

const isFormControlTarget = (target) => {
    const tag = target?.tagName;
    if (!tag) return false;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable === true;
};

const Metric = ({ label, value, valueColor }) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <Typography
                sx={{
                    fontFamily: '"Rethink Sans"',
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: 1.43,
                    letterSpacing: '0.17px',
                    color: 'rgba(0,0,0,0.6)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontFamily: '"Rethink Sans"',
                    fontWeight: 600,
                    fontSize: 15,
                    lineHeight: 1.57,
                    letterSpacing: '0.1px',
                    color: valueColor || 'rgba(0,0,0,0.87)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {value}
            </Typography>
        </Box>
    );
};

const ScenarioMetric = ({ label, value, valueColor }) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <Typography
                sx={{
                    fontFamily: '"Rethink Sans"',
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: 1.43,
                    letterSpacing: '0.17px',
                    color: 'rgba(0,0,0,0.6)',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontFamily: '"Rethink Sans"',
                    fontWeight: 600,
                    fontSize: 15,
                    lineHeight: 1.57,
                    letterSpacing: '0.1px',
                    color: valueColor || 'rgba(0,0,0,0.87)',
                    whiteSpace: 'nowrap',
                }}
            >
                {value}
            </Typography>
        </Box>
    );
};

const SectionRow = ({ title, total, disabled, expanded, items, totalLabel, onToggle }) => {
    const safeItems = Array.isArray(items) ? items : [];
    const showDetails = expanded && !disabled;
    return (
        <Box
            role="button"
            tabIndex={0}
            sx={{
                width: '100%',
                bgcolor: '#FDFDFD',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '10px',
                overflow: 'hidden',
                cursor: 'pointer',
                outline: 'none',
                '&:focus-visible': {
                    boxShadow: '0 0 0 3px rgba(83,109,236,0.25)',
                    borderColor: 'rgba(83,109,236,0.5)',
                },
                opacity: disabled ? 0.55 : 1,
            }}
            onClick={() => {
                if (disabled) return;
                onToggle?.();
            }}
            onKeyDown={(e) => {
                if (isFormControlTarget(e.target)) return;
                if (isKeyboardToggleEvent(e)) {
                    e.preventDefault();
                    if (disabled) return;
                    onToggle?.();
                }
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: '16px',
                    py: '8px',
                    bgcolor: showDetails ? '#F6F7FF' : 'transparent',
                }}
            >
                <Typography
                    sx={{
                        fontFamily: '"Rethink Sans"',
                        fontWeight: 600,
                        fontSize: 15,
                        lineHeight: 1.57,
                        letterSpacing: '0.1px',
                        color: 'rgba(0,0,0,0.87)',
                    }}
                >
                    {title}
                </Typography>
                <Stack direction="row" alignItems="center" sx={{ gap: '12px' }}>
                    <Typography
                        sx={{
                            fontFamily: '"Rethink Sans"',
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: 1.57,
                            letterSpacing: '0.1px',
                            color: disabled ? 'rgba(0,0,0,0.38)' : 'rgba(0,0,0,0.87)',
                        }}
                    >
                        {total}
                    </Typography>
                    <ExpandMoreIcon
                        sx={{
                            width: 24,
                            height: 24,
                            color: disabled ? 'rgba(0,0,0,0.38)' : 'rgba(0,0,0,0.54)',
                            transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 150ms ease',
                        }}
                    />
                </Stack>
            </Stack>

            {showDetails && (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', pt: '10px' }}>
                    {safeItems.length === 0 ? (
                        <Box sx={{ px: '16px', py: '10px' }}>
                            <Typography
                                sx={{
                                    fontFamily: '"Rethink Sans"',
                                    fontWeight: 400,
                                    fontSize: 14,
                                    lineHeight: 1.43,
                                    letterSpacing: '0.17px',
                                    color: 'rgba(0,0,0,0.6)',
                                }}
                            >
                                Loading...
                            </Typography>
                        </Box>
                    ) : (
                        safeItems.map((item, idx) => (
                            <Box
                                key={`${item.label}-${idx}`}
                                sx={{
                                    height: 30,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: '16px',
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: '"Rethink Sans"',
                                        fontWeight: 400,
                                        fontSize: 14,
                                        lineHeight: 1.43,
                                        letterSpacing: '0.17px',
                                        color: 'rgba(0,0,0,0.6)',
                                    }}
                                >
                                    {item.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: '"Rethink Sans"',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        lineHeight: 1.57,
                                        letterSpacing: '0.1px',
                                        color: 'rgba(0,0,0,0.87)',
                                    }}
                                >
                                    {`${formatMoney(item.amount)}/mo`}
                                </Typography>
                            </Box>
                        ))
                    )}

                    <Divider sx={{ borderColor: 'rgba(0,0,0,0.12)' }} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: '16px',
                            pb: '16px',
                            width: '100%',
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"Rethink Sans"',
                                fontWeight: 400,
                                fontSize: 14,
                                lineHeight: 1.43,
                                letterSpacing: '0.17px',
                                color: 'rgba(0,0,0,0.87)',
                            }}
                        >
                            {totalLabel || `Total ${title}`}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: '"Rethink Sans"',
                                fontWeight: 600,
                                fontSize: 14,
                                lineHeight: 1.57,
                                letterSpacing: '0.1px',
                                color: 'rgba(0,0,0,0.87)',
                            }}
                        >
                            {`${total}/mo`}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const ScenarioSectionRow = ({
    title,
    total,
    expanded,
    items,
    totalLabel,
    onToggle,
    getItemState,
    setItemState,
    onAddCategory,
}) => {
    const safeItems = Array.isArray(items) ? items : [];
    const showDetails = expanded;
    const [draftCategoryLabel, setDraftCategoryLabel] = useState('');
    const [draftCategoryAmount, setDraftCategoryAmount] = useState('');
    const [draftCategoryError, setDraftCategoryError] = useState(null);

    return (
        <Box
            role="button"
            tabIndex={0}
            sx={{
                width: '100%',
                bgcolor: '#FDFDFD',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '10px',
                overflow: 'hidden',
                cursor: 'pointer',
                outline: 'none',
                '&:focus-visible': {
                    boxShadow: '0 0 0 3px rgba(83,109,236,0.25)',
                    borderColor: 'rgba(83,109,236,0.5)',
                },
            }}
            onClick={() => onToggle?.()}
            onKeyDown={(e) => {
                if (isFormControlTarget(e.target)) return;
                if (isKeyboardToggleEvent(e)) {
                    e.preventDefault();
                    onToggle?.();
                }
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: '16px',
                    py: '8px',
                    bgcolor: showDetails ? '#F6F7FF' : 'transparent',
                }}
            >
                <Typography
                    sx={{
                        fontFamily: '"Rethink Sans"',
                        fontWeight: 600,
                        fontSize: 15,
                        lineHeight: 1.57,
                        letterSpacing: '0.1px',
                        color: 'rgba(0,0,0,0.87)',
                    }}
                >
                    {title}
                </Typography>
                <Stack direction="row" alignItems="center" sx={{ gap: '12px' }}>
                    <Typography
                        sx={{
                            fontFamily: '"Rethink Sans"',
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: 1.57,
                            letterSpacing: '0.1px',
                            color: 'rgba(0,0,0,0.87)',
                        }}
                    >
                        {total}
                    </Typography>
                    <ExpandMoreIcon
                        sx={{
                            width: 24,
                            height: 24,
                            color: 'rgba(0,0,0,0.54)',
                            transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 150ms ease',
                        }}
                    />
                </Stack>
            </Stack>

            {showDetails && (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', pt: '10px' }}>
                    {safeItems.map((item, idx) => {
                        const state = getItemState(item) || { enabled: true, value: item.amount };
                        const enabled = Boolean(state.enabled);
                        const value = Number.isFinite(Number(state.value)) ? Number(state.value) : item.amount;

                        const displayLabelColor = enabled ? 'rgba(0,0,0,0.87)' : 'rgba(0,0,0,0.38)';
                        const displayValueColor = enabled ? 'rgba(0,0,0,0.87)' : 'rgba(0,0,0,0.38)';

                        return (
                            <Box
                                key={`${item.label}-${idx}`}
                                sx={{
                                    height: 30,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: '16px',
                                    width: '100%',
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                    <Switch
                                        size="small"
                                        checked={enabled}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            const nextEnabled = e.target.checked;
                                            setItemState(item, {
                                                enabled: nextEnabled,
                                                value: nextEnabled ? value : item.amount,
                                            });
                                        }}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#536DEC' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#536DEC' },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: '"Rethink Sans"',
                                            fontWeight: 400,
                                            fontSize: 14,
                                            lineHeight: 1.43,
                                            letterSpacing: '0.17px',
                                            color: displayLabelColor,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: 240,
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Stack>

                                {!enabled ? (
                                    <Typography
                                        sx={{
                                            fontFamily: '"Rethink Sans"',
                                            fontWeight: 400,
                                            fontSize: 14,
                                            lineHeight: 1.43,
                                            letterSpacing: '0.17px',
                                            color: displayValueColor,
                                        }}
                                    >
                                        {formatMoney(item.amount)}
                                    </Typography>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 170 }} onClick={(e) => e.stopPropagation()}>
                                        <IconButton
                                            size="small"
                                            sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '4px', p: '4px' }}
                                            onClick={() => {
                                                setItemState(item, { enabled: true, value: clamp0(value - ADJUST_STEP) });
                                            }}
                                        >
                                            <RemoveIcon sx={{ width: 19.2, height: 19.2 }} />
                                        </IconButton>
                                        <TextField
                                            size="small"
                                            value={formatMoney(value)}
                                            onChange={(e) => {
                                                const raw = String(e.target.value || '').replace(/[^0-9.-]/g, '');
                                                const next = Number(raw);
                                                setItemState(item, { enabled: true, value: Number.isFinite(next) ? clamp0(next) : 0 });
                                            }}
                                            inputProps={{
                                                style: {
                                                    fontFamily: '"Rethink Sans"',
                                                    fontWeight: 400,
                                                    fontSize: 14,
                                                    lineHeight: 1.43,
                                                    letterSpacing: '0.17px',
                                                    padding: 0,
                                                },
                                            }}
                                            sx={{
                                                flex: '1 1 auto',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '4px',
                                                    px: '10px',
                                                    py: '2px',
                                                    height: 28,
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.08)' },
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '4px', p: '4px' }}
                                            onClick={() => {
                                                setItemState(item, { enabled: true, value: clamp0(value + ADJUST_STEP) });
                                            }}
                                        >
                                            <AddIcon sx={{ width: 19.2, height: 19.2 }} />
                                        </IconButton>
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}

                    {onAddCategory && (
                        <Box
                            sx={{
                                px: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <TextField
                                size="small"
                                placeholder="Add category..."
                                value={draftCategoryLabel}
                                onChange={(e) => {
                                    setDraftCategoryLabel(e.target.value);
                                    setDraftCategoryError(null);
                                }}
                                sx={{
                                    flex: '1 1 auto',
                                    '& .MuiOutlinedInput-root': {
                                        height: 32,
                                        borderRadius: '8px',
                                    },
                                }}
                                inputProps={{
                                    style: {
                                        fontFamily: '"Rethink Sans"',
                                        fontWeight: 400,
                                        fontSize: 14,
                                        lineHeight: 1.43,
                                        letterSpacing: '0.17px',
                                    },
                                }}
                            />
                            <TextField
                                size="small"
                                placeholder="$0"
                                value={draftCategoryAmount}
                                onChange={(e) => {
                                    setDraftCategoryAmount(e.target.value);
                                    setDraftCategoryError(null);
                                }}
                                sx={{
                                    width: 140,
                                    '& .MuiOutlinedInput-root': {
                                        height: 32,
                                        borderRadius: '8px',
                                    },
                                }}
                                inputProps={{
                                    style: {
                                        fontFamily: '"Rethink Sans"',
                                        fontWeight: 400,
                                        fontSize: 14,
                                        lineHeight: 1.43,
                                        letterSpacing: '0.17px',
                                    },
                                }}
                            />
                            <IconButton
                                size="small"
                                sx={{
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    borderRadius: '4px',
                                    p: '4px',
                                }}
                                onClick={() => {
                                    const label = String(draftCategoryLabel || '').trim();
                                    const raw = String(draftCategoryAmount || '').replace(/[^0-9.-]/g, '');
                                    const parsed = Number(raw);
                                    const amount = Number.isFinite(parsed) ? clamp0(parsed) : 0;

                                    if (!label) {
                                        setDraftCategoryError('Category name is required.');
                                        return;
                                    }
                                    const existingLabels = new Set(safeItems.map((it) => String(it?.label || '').trim().toLowerCase()));
                                    if (existingLabels.has(label.toLowerCase())) {
                                        setDraftCategoryError('That category already exists in this section.');
                                        return;
                                    }

                                    onAddCategory({ label, amount });
                                    setDraftCategoryLabel('');
                                    setDraftCategoryAmount('');
                                    setDraftCategoryError(null);
                                }}
                            >
                                <AddIcon sx={{ width: 19.2, height: 19.2 }} />
                            </IconButton>
                        </Box>
                    )}

                    {draftCategoryError && (
                        <Typography
                            sx={{
                                px: '16px',
                                fontFamily: '"Rethink Sans"',
                                fontSize: 13,
                                lineHeight: 1.43,
                                letterSpacing: '0.17px',
                                color: '#D32F2F',
                            }}
                        >
                            {draftCategoryError}
                        </Typography>
                    )}

                    <Divider sx={{ borderColor: 'rgba(0,0,0,0.12)' }} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: '16px',
                            pb: '16px',
                            width: '100%',
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"Rethink Sans"',
                                fontWeight: 400,
                                fontSize: 14,
                                lineHeight: 1.43,
                                letterSpacing: '0.17px',
                                color: 'rgba(0,0,0,0.87)',
                            }}
                        >
                            {totalLabel || `Total ${title}`}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: '"Rethink Sans"',
                                fontWeight: 600,
                                fontSize: 14,
                                lineHeight: 1.57,
                                letterSpacing: '0.1px',
                                color: 'rgba(0,0,0,0.87)',
                            }}
                        >
                            {total}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default function ScenarioList() {
    const navigate = useNavigate();
    const { scenarioId } = useParams();
    const isEditMode = Boolean(scenarioId);
    const [customerId, setCustomerId] = useState(() => sessionStorage.getItem('selectedCustomerId'));
    const endDateStr = useMemo(() => toDateStr(new Date()), []);
    const [expandedSection, setExpandedSection] = useState(null);
    const [scenarioExpandedSections, setScenarioExpandedSections] = useState(['Income']);
    const [breakdowns, setBreakdowns] = useState({});
    const requestedBreakdownsRef = useRef(new Set());
    const [accounts, setAccounts] = useState([]);
    const [pageTitle, setPageTitle] = useState('');
    const [scenarioName, setScenarioName] = useState('');
    const [scenarioDescription, setScenarioDescription] = useState('');
    const [scenarioOverrides, setScenarioOverrides] = useState({});
    const [scenarioAddedCategories, setScenarioAddedCategories] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [baselineEndDate, setBaselineEndDate] = useState('');

    const [baseline, setBaseline] = useState({
        netProfit: 0,
        profitMarginPct: 0,
        revenue: 0,
        expenses: 0,
    });
    const [baselineMode, setBaselineMode] = useState('historical');
    const [currentBaselineLoading, setCurrentBaselineLoading] = useState(false);
    const [currentBaseline, setCurrentBaseline] = useState({
        netProfit: 0,
        profitMarginPct: 0,
        revenue: 0,
        expenses: 0,
    });
    const [currentBreakdowns, setCurrentBreakdowns] = useState({});
    const currentRequestedBreakdownsRef = useRef(new Set());
    const baselineHeadline = baselineMode === 'current' ? currentBaseline : baseline;
    const displayBreakdowns = baselineMode === 'current' ? currentBreakdowns : breakdowns;

    const sections = useMemo(
        () => [
            { title: 'Income', group: 'income', totalFallback: 0, ranges: [[4000, 4999]] },
            { title: 'Cost of Goods Sold', group: 'cogs', totalFallback: 0, ranges: [[5000, 5999]] },
            { title: 'Advertising & Marketing', group: 'advertising_marketing', totalFallback: 0, ranges: [[6100, 6199]] },
            { title: 'Team', group: 'team', totalFallback: 0, ranges: [[6200, 6299]] },
            { title: 'Practice Costs', group: 'practice_costs', totalFallback: 0, ranges: [[6600, 6799]] },
            { title: 'Overheads', group: 'overheads', totalFallback: 0, ranges: [[6800, 6999]] },
            { title: 'Business Development', group: 'business_development', totalFallback: 0, ranges: [[7000, 7499]] },
            { title: 'Taxes', group: 'taxes', totalFallback: 0, ranges: [[7500, 7999]] },
            { title: 'Other Income', group: 'other_income', totalFallback: 0, ranges: [[8000, 8999]] },
            { title: 'Other Expenses', group: 'other_expense', totalFallback: 0, ranges: [[9000, 9999]] },
        ],
        []
    );

    useEffect(() => {
        requestedBreakdownsRef.current = new Set();
        currentRequestedBreakdownsRef.current = new Set();
        setBreakdowns({});
        setCurrentBreakdowns({});
        setExpandedSection(null);
        setScenarioExpandedSections(['Income']);
        setAccounts([]);
        setScenarioOverrides({});
        setScenarioAddedCategories({});
        setLoadError(null);
        setBaselineMode('historical');
    }, [customerId, endDateStr]);

    useEffect(() => {
        const syncFromStorage = () => {
            setCustomerId(sessionStorage.getItem('selectedCustomerId'));
        };
        window.addEventListener('selectedCustomerId-changed', syncFromStorage);
        return () => window.removeEventListener('selectedCustomerId-changed', syncFromStorage);
    }, []);

    useEffect(() => {
        if (!customerId) return;
        const controller = new AbortController();

        const loadAccounts = async () => {
            try {
                // Accounts
                const [childrenRes, parentsRes] = await Promise.all([
                    fetchCustomerAccounts(customerId, { namespace: 'new', is_main_account: false }, false),
                    fetchCustomerAccounts(customerId, { namespace: 'new', is_main_account: true }, false),
                ]);

                const rawChildren = childrenRes?.data?.results ?? childrenRes?.data ?? [];
                const rawParents = parentsRes?.data?.results ?? parentsRes?.data ?? [];
                const merged = []
                    .concat(Array.isArray(rawParents) ? rawParents : [])
                    .concat(Array.isArray(rawChildren) ? rawChildren : []);

                const seen = new Set();
                const normalized = merged
                    .filter((a) => a && a.name && a.number)
                    .map((a) => ({
                        id: a.id,
                        number: String(a.number),
                        name: String(a.name),
                        is_main_account: Boolean(a.is_main_account),
                    }))
                    .filter((a) => {
                        const key = `${a.number}-${a.name}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });

                if (!controller.signal.aborted) {
                    setAccounts(normalized);
                }
            } catch (err) {
                if (!controller.signal.aborted) {
                    setAccounts([]);
                }
            }
        };

        loadAccounts();
        return () => controller.abort();
    }, [customerId]);

    useEffect(() => {
        if (isEditMode) return;
        if (!customerId) return;
        const controller = new AbortController();

        listCustomerScenarios(customerId, {}, controller.signal)
            .then((res) => {
                const rows = res?.data;
                const nextNum = Array.isArray(rows) ? rows.length + 1 : 1;
                const defaultName = buildDefaultScenarioName(nextNum, endDateStr);
                setPageTitle((prev) => prev || defaultName);
                setScenarioName((prev) => prev || defaultName);
            })
            .catch(() => {
                const defaultName = buildDefaultScenarioName(1, endDateStr);
                setPageTitle((prev) => prev || defaultName);
                setScenarioName((prev) => prev || defaultName);
            });

        return () => controller.abort();
    }, [customerId, endDateStr, isEditMode]);

    useEffect(() => {
        if (!isEditMode) return;
        if (!customerId || !scenarioId) return;
        const controller = new AbortController();
        setLoadError(null);

        getCustomerScenario(customerId, scenarioId, {}, controller.signal)
            .then((res) => {
                if (controller.signal.aborted) return;
                const s = res?.data || {};

                setPageTitle(String(s.name || 'Scenario'));
                setScenarioName(String(s.name || ''));
                setScenarioDescription(String(s.description || ''));
                setBaselineEndDate(String(s.baseline_end_date || ''));

                const baselineMetrics = s.baseline_metrics || {};
                setBaseline({
                    netProfit: Number(baselineMetrics.netProfit) || 0,
                    profitMarginPct: Number(baselineMetrics.profitMarginPct) || 0,
                    revenue: Number(baselineMetrics.revenue) || 0,
                    expenses: Number(baselineMetrics.expenses) || 0,
                });

                const baselineBreakdowns = s.baseline_breakdowns || {};
                const nextBreakdowns = {};
                const loadedGroups = new Set();
                Object.keys(baselineBreakdowns || {}).forEach((group) => {
                    const payload = baselineBreakdowns[group] || {};
                    loadedGroups.add(group);
                    nextBreakdowns[group] = {
                        loaded: true,
                        loading: false,
                        items: Array.isArray(payload.items)
                            ? payload.items.map((it) => ({ label: it?.label ?? '', amount: Number(it?.amount) || 0 }))
                            : [],
                        accounts: Array.isArray(payload.accounts)
                            ? payload.accounts.map((it) => ({
                                  number: it?.number ?? '',
                                  label: it?.label ?? '',
                                  amount: Number(it?.amount) || 0,
                              }))
                            : [],
                        total: Number(payload.total) || 0,
                    };
                });
                requestedBreakdownsRef.current = loadedGroups;
                setBreakdowns(nextBreakdowns);

                const lineItems = Array.isArray(s.line_items) ? s.line_items : [];
                const nextOverrides = {};
                lineItems.forEach((li) => {
                    const group = li?.group_key;
                    const label = li?.label;
                    if (!group || !label) return;
                    nextOverrides[`${group}:${label}`] = {
                        enabled: Boolean(li?.is_enabled),
                        value: Number(li?.scenario_amount) || 0,
                    };
                });
                setScenarioOverrides(nextOverrides);

                const baselineLabelsByGroup = new Map();
                Object.entries(nextBreakdowns).forEach(([group, payload]) => {
                    const labels = new Set();
                    (payload?.items || []).forEach((it) => labels.add(String(it?.label || '').trim().toLowerCase()));
                    (payload?.accounts || []).forEach((it) => labels.add(String(it?.label || '').trim().toLowerCase()));
                    baselineLabelsByGroup.set(group, labels);
                });
                const nextAdded = {};
                lineItems.forEach((li) => {
                    const group = li?.group_key;
                    const label = String(li?.label || '').trim();
                    if (!group || !label) return;
                    const baselineLabels = baselineLabelsByGroup.get(group);
                    if (baselineLabels && baselineLabels.has(label.toLowerCase())) return;
                    const existing = Array.isArray(nextAdded[group]) ? nextAdded[group] : [];
                    nextAdded[group] = existing.concat({ label, amount: 0 });
                });
                setScenarioAddedCategories(nextAdded);
            })
            .catch((err) => {
                if (controller.signal.aborted) return;
                const data = err?.response?.data;
                if (data?.error && data?.details) {
                    setLoadError(`${data.error} ${JSON.stringify(data.details)}`);
                } else if (data?.error) {
                    setLoadError(String(data.error));
                } else {
                    setLoadError('Failed to load scenario.');
                }
            });

        return () => controller.abort();
    }, [customerId, isEditMode, scenarioId]);

    useEffect(() => {
        if (!isEditMode) return;
        if (baselineMode !== 'current') return;
        if (!customerId) return;

        const controller = new AbortController();
        setCurrentBaselineLoading(true);

        getMetricData(customerId, endDateStr, controller.signal, { adjusted: false, ytd: false })
            .then((res) => {
                if (controller.signal.aborted) return;
                const data = res?.data || {};
                setCurrentBaseline({
                    netProfit: Number(data.profit) || 0,
                    profitMarginPct: Number(data.profit_percentage) || 0,
                    revenue: Number(data.income) || 0,
                    expenses: Number(data.expense) || 0,
                });
            })
            .catch(() => {
                if (!controller.signal.aborted) {
                    setCurrentBaseline({
                        netProfit: 0,
                        profitMarginPct: 0,
                        revenue: 0,
                        expenses: 0,
                    });
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setCurrentBaselineLoading(false);
            });

        const groups = sections.map((s) => s.group).filter(Boolean);
        groups.forEach((group) => {
            if (currentRequestedBreakdownsRef.current.has(group)) return;
            currentRequestedBreakdownsRef.current.add(group);

            setCurrentBreakdowns((prev) => ({
                ...prev,
                [group]: { ...(prev[group] || {}), loading: true },
            }));

            getCategoryBreakdown(customerId, endDateStr, group, controller.signal)
                .then((res) => {
                    if (controller.signal.aborted) return;
                    const items = Array.isArray(res?.data?.items) ? res.data.items : [];
                    const accounts = Array.isArray(res?.data?.accounts) ? res.data.accounts : [];
                    const total = res?.data?.total;

                    setCurrentBreakdowns((prev) => ({
                        ...prev,
                        [group]: {
                            loaded: true,
                            loading: false,
                            items: items.map((it) => ({
                                label: it?.label ?? '',
                                amount: Number(it?.amount) || 0,
                            })),
                            accounts: accounts.map((it) => ({
                                number: it?.number ?? '',
                                label: it?.label ?? '',
                                amount: Number(it?.amount) || 0,
                            })),
                            total: Number(total) || 0,
                        },
                    }));
                })
                .catch(() => {
                    if (!controller.signal.aborted) {
                        setCurrentBreakdowns((prev) => ({
                            ...prev,
                            [group]: { ...(prev[group] || {}), loaded: true, loading: false, items: [], accounts: [], total: 0 },
                        }));
                    }
                });
        });

        return () => controller.abort();
    }, [baselineMode, customerId, endDateStr, isEditMode, sections]);

    useEffect(() => {
        if (isEditMode) return;
        if (!customerId) return;
        const controller = new AbortController();
        const fetchBaseline = async () => {
            try {
                const res = await getMetricData(customerId, endDateStr, controller.signal, { adjusted: false, ytd: false });
                const data = res?.data || {};
                setBaseline({
                    netProfit: Number(data.profit) || 0,
                    profitMarginPct: Number(data.profit_percentage) || 0,
                    revenue: Number(data.income) || 0,
                    expenses: Number(data.expense) || 0,
                });
            } catch (err) {
                if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
                    console.error('Failed to load baseline metrics', err);
                    setLoadError((prev) => prev || 'Failed to load baseline metrics.');
                }
            }
        };
        fetchBaseline();
        return () => controller.abort();
    }, [customerId, endDateStr, isEditMode]);

    useEffect(() => {
        if (isEditMode) return;
        if (!customerId) return;
        const controller = new AbortController();
        const groups = sections.map((s) => s.group).filter(Boolean);

        groups.forEach((group) => {
            if (requestedBreakdownsRef.current.has(group)) return;
            requestedBreakdownsRef.current.add(group);

            setBreakdowns((prev) => ({
                ...prev,
                [group]: { ...(prev[group] || {}), loading: true },
            }));

            getCategoryBreakdown(customerId, endDateStr, group, controller.signal)
                .then((res) => {
                    if (controller.signal.aborted) return;
                    const items = Array.isArray(res?.data?.items) ? res.data.items : [];
                    const accounts = Array.isArray(res?.data?.accounts) ? res.data.accounts : [];
                    const total = res?.data?.total;
                    setBreakdowns((prev) => ({
                        ...prev,
                        [group]: {
                            loaded: true,
                            loading: false,
                            items: items.map((it) => ({
                                label: it?.label ?? '',
                                amount: Number(it?.amount) || 0,
                            })),
                            accounts: accounts.map((it) => ({
                                number: it?.number ?? '',
                                label: it?.label ?? '',
                                amount: Number(it?.amount) || 0,
                            })),
                            total: Number(total) || 0,
                        },
                    }));
                })
                .catch(() => {
                    if (!controller.signal.aborted) {
                        setBreakdowns((prev) => ({
                            ...prev,
                            [group]: { ...(prev[group] || {}), loaded: true, loading: false, items: [], accounts: [], total: 0 },
                        }));
                    }
                });
        });

        return () => controller.abort();
    }, [customerId, endDateStr, sections, isEditMode]);

    useEffect(() => {
        if (isEditMode) return;
        if (!customerId || !expandedSection) return;
        const section = sections.find((s) => s.title === expandedSection);
        const group = section?.group;
        if (!group) return;
        if (requestedBreakdownsRef.current.has(group)) return;
        requestedBreakdownsRef.current.add(group);

        const controller = new AbortController();
        setBreakdowns((prev) => ({
            ...prev,
            [group]: { ...(prev[group] || {}), loading: true },
        }));

        getCategoryBreakdown(customerId, endDateStr, group, controller.signal)
            .then((res) => {
                if (controller.signal.aborted) return;
                const items = Array.isArray(res?.data?.items) ? res.data.items : [];
                const accounts = Array.isArray(res?.data?.accounts) ? res.data.accounts : [];
                const total = res?.data?.total;

                setBreakdowns((prev) => ({
                    ...prev,
                    [group]: {
                        loaded: true,
                        loading: false,
                        items: items.map((it) => ({
                            label: it?.label ?? '',
                            amount: Number(it?.amount) || 0,
                        })),
                        accounts: accounts.map((it) => ({
                            number: it?.number ?? '',
                            label: it?.label ?? '',
                            amount: Number(it?.amount) || 0,
                        })),
                        total: Number(total) || 0,
                    },
                }));
            })
            .catch(() => {
                if (!controller.signal.aborted) {
                    setBreakdowns((prev) => ({
                        ...prev,
                        [group]: { ...(prev[group] || {}), loaded: true, loading: false, items: [], accounts: [], total: 0 },
                    }));
                }
            });

        return () => controller.abort();
    }, [customerId, endDateStr, expandedSection, sections, isEditMode]);

    return (
        <ClientDashboardLayout title={null} contentMaxWidth="100%" contentAlign="left">
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 2.5, sm: 3 },
                }}
            >
                <Box sx={{ px: RESPONSIVE.headerPaddingX, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Button
                        variant="text"
                        startIcon={<ArrowBackIcon sx={{ width: 24, height: 24 }} />}
                        onClick={() => navigate('/scenario-planner')}
                        sx={{
                            alignSelf: 'flex-start',
                            textTransform: 'capitalize',
                            px: '0px',
                            py: '6px',
                            borderRadius: '4px',
                            fontFamily: '"Rethink Sans"',
                            fontWeight: 500,
                            fontSize: 14,
                            lineHeight: '24px',
                            letterSpacing: '0.4px',
                            color: '#536DEC',
                            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                            '& .MuiButton-startIcon': { ml: 0, mr: '8px' },
                        }}
                    >
                        Back to Scenarios
                    </Button>

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 2.5, flexWrap: 'wrap' }}>
                        <Typography
                            sx={{
                                fontFamily: '"Rethink Sans"',
                                fontSize: RESPONSIVE.pageTitleFontSize,
                                fontWeight: 600,
                                lineHeight: RESPONSIVE.pageTitleLineHeight,
                                color: 'rgba(0,0,0,0.87)',
                            }}
                        >
                            {pageTitle || scenarioName || buildDefaultScenarioName(1, endDateStr)}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="contained"
                                startIcon={<SaveOutlinedIcon sx={{ width: 24, height: 24 }} />}
                                sx={{
                                    textTransform: 'capitalize',
                                    borderRadius: '4px',
                                    px: RESPONSIVE.primaryButtonPaddingX,
                                    py: RESPONSIVE.primaryButtonPaddingY,
                                    fontFamily: '"Rethink Sans"',
                                    fontWeight: 500,
                                    fontSize: RESPONSIVE.primaryButtonFontSize,
                                    lineHeight: '24px',
                                    letterSpacing: '0.4px',
                                    boxShadow: '0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.20)',
                                    bgcolor: '#536DEC',
                                    '&:hover': { bgcolor: '#4C63D7' },
                                    '& .MuiButton-startIcon': { ml: 0, mr: '8px' },
                                }}
                                    disabled={saving}
                                onClick={() => {
                                        if (!customerId) return;
                                        setSaving(true);
                                        setSaveError(null);

                                        const scenarioNameToSave = scenarioName || buildDefaultScenarioName(1, endDateStr);
                                        const baselineEndDateToSave = isEditMode ? baselineEndDate : endDateStr;

                                        const toDecimalStr = (n) => {
                                            const v = Number(n || 0);
                                            if (!Number.isFinite(v)) return '0.00';
                                            return v.toFixed(2);
                                        };

                                        const baselineBreakdowns = {};
                                        sections.forEach((section) => {
                                            const group = section.group;
                                            if (!group) return;
                                            const data = breakdowns[group];
                                            if (!data?.loaded) return;
                                            baselineBreakdowns[group] = {
                                                group,
                                                total: data.total,
                                                items: data.items,
                                                accounts: data.accounts,
                                            };
                                        });

                                        const lineItems = [];
                                        const accountNumberFromExistingLineItems = new Map();
                                        if (isEditMode) {
                                            sections.forEach((section) => {
                                                const group = section.group;
                                                if (!group) return;
                                                const data = breakdowns[group];
                                                const existingAccounts = data?.accounts || [];
                                                existingAccounts.forEach((it) => {
                                                    if (!it?.label || !it?.number) return;
                                                    accountNumberFromExistingLineItems.set(`${group}:${String(it.label)}`, String(it.number));
                                                });
                                            });
                                        }
                                        sections.forEach((section) => {
                                            const group = section.group;
                                            if (!group) return;
                                            const data = breakdowns[group];
                                            if (!data?.loaded) return;

                                            const accountsNumberByLabel = new Map(
                                                (data?.accounts || []).map((it) => [String(it.label), String(it.number)])
                                            );
                                            const accountsAmountByLabel = new Map(
                                                (data?.accounts || []).map((it) => [String(it.label), Number(it.amount) || 0])
                                            );

                                            const baseItems = (data?.items || []).map((it) => ({
                                                label: it.label,
                                                amount: accountsAmountByLabel.get(String(it.label)) || 0,
                                            }));
                                            const baseLabels = new Set(baseItems.map((it) => it.label));
                                            const savedAccounts = (data?.accounts || [])
                                                .filter((it) => it.amount && !baseLabels.has(it.label))
                                                .map((it) => ({ label: it.label, amount: Number(it.amount) || 0 }));
                                            const sectionRanges = section.ranges || [];
                                            const children = isEditMode
                                                ? []
                                                : (accounts || [])
                                                      .filter((a) => !a.is_main_account)
                                                      .filter((a) => inAnyRange(toAccountNumber(a.number), sectionRanges))
                                                      .map((a) => ({ label: a.name, amount: accountsAmountByLabel.get(String(a.name)) || 0 }));
                                            const addedCategories = scenarioAddedCategories[group] || [];

                                            const seen = new Set();
                                            const allItems = []
                                                .concat(baseItems)
                                                .concat(savedAccounts)
                                                .concat(children)
                                                .concat(addedCategories.map((it) => ({ label: it.label, amount: 0 })))
                                                .filter((it) => {
                                                    if (!it?.label) return false;
                                                    if (seen.has(it.label)) return false;
                                                    seen.add(it.label);
                                                    return true;
                                                });

                                            allItems.forEach((item, idx) => {
                                                const key = `${group}:${item.label}`;
                                                const state = scenarioOverrides[key];
                                                const enabled = state ? Boolean(state.enabled) : true;
                                                const rawValue = Number.isFinite(Number(state?.value)) ? Number(state.value) : item.amount;
                                                const scenarioAmount = enabled ? rawValue : item.amount;
                                                const accountNumber =
                                                    accountsNumberByLabel.get(String(item.label)) ||
                                                    accountNumberFromExistingLineItems.get(`${group}:${String(item.label)}`) ||
                                                    null;

                                                lineItems.push({
                                                    group_key: group,
                                                    account_number: accountNumber,
                                                    label: item.label,
                                                    baseline_amount: toDecimalStr(item.amount),
                                                    scenario_amount: toDecimalStr(scenarioAmount),
                                                    is_enabled: enabled,
                                                    sort_order: idx,
                                                });
                                            });
                                        });

                                        const totalsBySectionTitle = new Map();
                                        sections.forEach((section) => {
                                            const group = section.group;
                                            if (!group) return;
                                            const itemsForGroup = lineItems.filter((it) => it.group_key === group);
                                            const total = itemsForGroup.reduce((sum, it) => sum + Number(it.scenario_amount || 0), 0);
                                            totalsBySectionTitle.set(section.title, total);
                                        });
                                        const revenue = (totalsBySectionTitle.get('Income') || 0) + (totalsBySectionTitle.get('Other Income') || 0);
                                        const expenses =
                                            (totalsBySectionTitle.get('Cost of Goods Sold') || 0) +
                                            (totalsBySectionTitle.get('Advertising & Marketing') || 0) +
                                            (totalsBySectionTitle.get('Team') || 0) +
                                            (totalsBySectionTitle.get('Practice Costs') || 0) +
                                            (totalsBySectionTitle.get('Overheads') || 0) +
                                            (totalsBySectionTitle.get('Business Development') || 0) +
                                            (totalsBySectionTitle.get('Taxes') || 0) +
                                            (totalsBySectionTitle.get('Other Expenses') || 0);
                                        const profit = revenue - expenses;
                                        const margin = revenue ? (profit / revenue) * 100 : 0;

                                        const payload = {
                                            name: scenarioNameToSave,
                                            description: scenarioDescription,
                                            baseline_end_date: baselineEndDateToSave,
                                            baseline_metrics: baseline,
                                            baseline_breakdowns: baselineBreakdowns,
                                            scenario_metrics: {
                                                netProfit: profit,
                                                profitMarginPct: margin,
                                                revenue,
                                                expenses,
                                            },
                                            line_items: lineItems,
                                        };

                                        const request = isEditMode
                                            ? updateCustomerScenario(customerId, scenarioId, payload)
                                            : createCustomerScenario(customerId, payload);

                                        request
                                            .then((res) => {
                                                const id = isEditMode ? scenarioId : res?.data?.id;
                                                if (!id) {
                                                    setSaveError('Scenario saved, but no scenario id was returned.');
                                                    return;
                                                }
                                                navigate('/scenario-planner');
                                            })
                                            .catch((err) => {
                                                const data = err?.response?.data;
                                                if (data?.error && data?.details) {
                                                    setSaveError(`${data.error} ${JSON.stringify(data.details)}`);
                                                } else if (data?.error) {
                                                    setSaveError(String(data.error));
                                                } else {
                                                    setSaveError(isEditMode ? 'Failed to update scenario.' : 'Failed to save scenario.');
                                                }
                                            })
                                            .finally(() => {
                                                setSaving(false);
                                            });
                                }}
                            >
                                {isEditMode ? 'Update Scenario' : 'Create Scenario'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<RemoveRedEyeOutlinedIcon sx={{ width: 24, height: 24 }} />}
                                sx={{
                                    textTransform: 'capitalize',
                                    borderRadius: '8px',
                                    px: '16px',
                                    py: '9px',
                                    fontFamily: '"Rethink Sans"',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    lineHeight: '24px',
                                    letterSpacing: '0.4px',
                                    borderColor: 'rgba(83,109,236,0.5)',
                                    color: '#536DEC',
                                    bgcolor: '#FFFFFF',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        borderColor: 'rgba(83,109,236,0.7)',
                                        bgcolor: 'rgba(83,109,236,0.04)',
                                        boxShadow: 'none',
                                    },
                                    '& .MuiButton-startIcon': { ml: 0, mr: '8px' },
                                }}
                                onClick={() => {
                                }}
                            >
                                View Comparison
                            </Button>
                        </Stack>
                    </Stack>
                    {loadError && (
                        <Typography sx={{ mt: '8px', fontFamily: '"Rethink Sans"', fontSize: 13, color: '#D32F2F' }}>
                            {loadError}
                        </Typography>
                    )}
                    {saveError && (
                        <Typography sx={{ mt: '8px', fontFamily: '"Rethink Sans"', fontSize: 13, color: '#D32F2F' }}>
                            {saveError}
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ mt: '14px', mb: '16px' }} />

                <Box
                    sx={{
                        px: RESPONSIVE.headerPaddingX,
                        display: 'flex',
                        gap: '20px',
                        width: '100%',
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        alignItems: 'stretch',
                    }}
                >
                    <Card
                        sx={{
                            width: { xs: '100%', lg: 520 },
                            minWidth: { xs: '100%', lg: 520 },
                            flex: { xs: '1 1 100%', lg: '0 0 auto' },
                            minHeight: { lg: PANEL_MIN_HEIGHT_LG },
                            height: { lg: PANEL_HEIGHT_LG },
                            maxHeight: { lg: PANEL_HEIGHT_LG },
                            borderRadius: '12px',
                            border: '1px solid rgba(0,0,0,0.10)',
                            boxShadow: 'none',
                            overflowY: { lg: 'auto' },
                            overflowX: 'hidden',
                        }}
                    >
                        <Box sx={{ position: 'sticky', top: 0, bgcolor: 'white', zIndex: 2 }}>
                            <Box sx={{ px: RESPONSIVE.panelInnerPaddingX, py: '20px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ gap: '12px' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: '"Rethink Sans"',
                                                fontWeight: 600,
                                                fontSize: 16,
                                                lineHeight: '32px',
                                                letterSpacing: '0.15px',
                                                color: 'rgba(0,0,0,0.87)',
                                            }}
                                        >
                                            Your Current Performance
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: '"Rethink Sans"',
                                                fontWeight: 400,
                                                fontSize: 14,
                                                lineHeight: '24px',
                                                letterSpacing: '0.17px',
                                                color: 'rgba(0,0,0,0.6)',
                                            }}
                                        >
                                            Actual baseline metrics
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', flexShrink: 0 }}>
                                        <Box sx={{ height: '32px', display: 'flex', alignItems: 'center' }}>
                                            <Chip
                                                icon={<LockIcon sx={{ width: 20, height: 20, color: 'rgba(0,0,0,0.56)' }} />}
                                                label="Baseline"
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(0,0,0,0.08)',
                                                    color: 'rgba(0,0,0,0.56)',
                                                    fontFamily: '"Rethink Sans"',
                                                    fontWeight: 500,
                                                    fontSize: 13,
                                                    lineHeight: '18px',
                                                    letterSpacing: '0.16px',
                                                    borderRadius: '999px',
                                                    px: '8px',
                                                    py: '3px',
                                                    '& .MuiChip-icon': { ml: 0, mr: '6px' },
                                                }}
                                            />
                                        </Box>

                                        {isEditMode ? (
                                            <Box sx={{ height: '24px', display: 'flex', alignItems: 'center' }}>
                                                <ToggleButtonGroup
                                                    exclusive
                                                    value={baselineMode}
                                                    onChange={(_, next) => {
                                                        if (!next) return;
                                                        setBaselineMode(next);
                                                    }}
                                                    size="small"
                                                    sx={{
                                                        height: 24,
                                                        bgcolor: 'rgba(0,0,0,0.06)',
                                                        p: '2px',
                                                        borderRadius: '12px',
                                                        '& .MuiToggleButtonGroup-grouped': {
                                                            border: 0,
                                                            minWidth: 0,
                                                            px: '8px',
                                                            py: 0,
                                                            borderRadius: '10px',
                                                            textTransform: 'none',
                                                            fontFamily: '"Rethink Sans"',
                                                            fontWeight: 500,
                                                            fontSize: 13,
                                                            lineHeight: '20px',
                                                            letterSpacing: '0.16px',
                                                            color: 'rgba(0,0,0,0.6)',
                                                            '&.Mui-selected': {
                                                                bgcolor: '#FFFFFF',
                                                                color: 'rgba(0,0,0,0.87)',
                                                                boxShadow: '0px 1px 2px rgba(0,0,0,0.12)',
                                                            },
                                                            '&.Mui-selected:hover': { bgcolor: '#FFFFFF' },
                                                        },
                                                    }}
                                                >
                                                    <ToggleButton value="historical">Historical</ToggleButton>
                                                    <ToggleButton value="current">Current</ToggleButton>
                                                </ToggleButtonGroup>
                                            </Box>
                                        ) : (
                                            <Box sx={{ height: '24px' }} />
                                        )}
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{ px: RESPONSIVE.panelInnerPaddingX, pt: '16px' }}>
                                <Box
                                    sx={{
                                        bgcolor: '#F8F9FC',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        borderRadius: '10px',
                                        p: { xs: '16px', xl: '20px' },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: '"Rethink Sans"',
                                            fontWeight: 500,
                                            fontSize: 14,
                                            lineHeight: 1.43,
                                            letterSpacing: '0.17px',
                                            textTransform: 'uppercase',
                                            color: 'rgba(0,0,0,0.87)',
                                            mb: '12px',
                                        }}
                                    >
                                        Current Performance
                                    </Typography>

                                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ gap: '12px' }}>
                                        <Metric
                                            label="Net Profit"
                                            value={formatMoney(baselineHeadline.netProfit)}
                                            valueColor={baselineHeadline.netProfit < 0 ? '#D32F2F' : '#2E7D32'}
                                        />
                                        <Divider orientation="vertical" flexItem />
                                        <Metric label="Profit Margin" value={formatPercent1(baselineHeadline.profitMarginPct)} />
                                        <Divider orientation="vertical" flexItem />
                                        <Metric label="Revenue" value={formatMoney(baselineHeadline.revenue)} />
                                        <Divider orientation="vertical" flexItem />
                                        <Metric label="Expenses" value={formatMoney(baselineHeadline.expenses)} />
                                    </Stack>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ px: RESPONSIVE.panelInnerPaddingX, py: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {isEditMode && baselineMode === 'current' && currentBaselineLoading ? (
                                <Typography sx={{ fontFamily: '"Rethink Sans"', fontSize: 14, color: 'rgba(0,0,0,0.6)' }}>
                                    Loading current baseline...
                                </Typography>
                            ) : null}
                            {sections.map((section) => {
                                const group = section.group;
                                const data = group ? displayBreakdowns[group] : null;
                                const totalValue = data?.loaded ? data?.total : section.totalFallback;
                                const items = data?.loaded ? data?.items : [];
                                const baseLabels = new Set(items.map((it) => it.label));
                                const amountByNumber = new Map((data?.accounts || []).map((it) => [String(it.number), Number(it.amount) || 0]));

                                const extraFromBreakdown = data?.loaded
                                    ? (data?.accounts || []).filter((it) => it.amount && !baseLabels.has(it.label))
                                    : [];

                                const sectionRanges = section.ranges || [];
                                const accountsForSection = (accounts || [])
                                    .filter((a) => inAnyRange(toAccountNumber(a.number), sectionRanges))
                                    .map((a) => ({
                                        label: a.name,
                                        amount: amountByNumber.get(String(a.number)) || 0,
                                    }))
                                    .filter((a) => !baseLabels.has(a.label))
                                    .sort((a, b) => a.label.localeCompare(b.label));

                                const combinedItems = items
                                    .concat(extraFromBreakdown.map((it) => ({ label: it.label, amount: it.amount })))
                                    .concat(accountsForSection);
                                return (
                                    <SectionRow
                                        key={section.title}
                                        title={section.title}
                                        total={formatMoney(totalValue)}
                                        disabled={!group}
                                        expanded={expandedSection === section.title}
                                        items={combinedItems}
                                        totalLabel={`Total ${section.title}`}
                                        onToggle={() => {
                                            setExpandedSection((prev) => (prev === section.title ? null : section.title));
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </Card>

                    <Card
                        sx={{
                            width: { xs: '100%', lg: 520 },
                            minWidth: { xs: '100%', lg: 520 },
                            flex: { xs: '1 1 100%', lg: '0 0 auto' },
                            minHeight: { lg: PANEL_MIN_HEIGHT_LG },
                            height: { lg: PANEL_HEIGHT_LG },
                            maxHeight: { lg: PANEL_HEIGHT_LG },
                            borderRadius: '12px',
                            border: '1px solid #536DEC',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '16px', pb: '16px' }}>
                            <Box sx={{ position: 'sticky', top: 0, bgcolor: 'white', zIndex: 2, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <Box sx={{ px: '16px', py: '14px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                    <Stack sx={{ gap: '8px' }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Scenario Name eg. “New Doctor Hire”..."
                                            value={scenarioName}
                                            onChange={(e) => setScenarioName(limitToWords(e.target.value, SCENARIO_NAME_MAX_WORDS))}
                                            sx={{
                                                '& .MuiOutlinedInput-root': { borderRadius: '4px', height: 34 },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '6px 12px',
                                                    fontFamily: '"Rethink Sans"',
                                                    fontWeight: 600,
                                                    fontSize: 14,
                                                    lineHeight: 1.57,
                                                    letterSpacing: '0.1px',
                                                    color: 'rgba(0,0,0,0.87)',
                                                },
                                            }}
                                        />
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Input Scenario Description..."
                                            value={scenarioDescription}
                                            onChange={(e) => setScenarioDescription(limitToWords(e.target.value, SCENARIO_DESCRIPTION_MAX_WORDS))}
                                            sx={{
                                                '& .MuiOutlinedInput-root': { borderRadius: '4px', height: 31 },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '7px 12px',
                                                    fontFamily: '"Rethink Sans"',
                                                    fontSize: 12,
                                                    letterSpacing: '0.17px',
                                                    lineHeight: 1.43,
                                                    color: 'rgba(0,0,0,0.87)',
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Box>

                                {(() => {
                                    const sectionTotals = new Map();
                                    sections.forEach((section) => {
                                        const group = section.group;
                                        if (!group) return;
                                        const data = breakdowns[group];
                                        const accountsAmountByLabel = new Map(
                                            (data?.accounts || []).map((it) => [String(it.label), Number(it.amount) || 0])
                                        );
                                        const baseItems = (data?.items || []).map((it) => ({
                                            label: it.label,
                                            amount: accountsAmountByLabel.get(String(it.label)) || 0,
                                        }));
                                        const baseLabels = new Set(baseItems.map((it) => it.label));
                                        const savedAccounts = (data?.accounts || [])
                                            .filter((it) => it.amount && !baseLabels.has(it.label))
                                            .map((it) => ({ label: it.label, amount: Number(it.amount) || 0 }));
                                        const sectionRanges = section.ranges || [];
                                        const children = isEditMode
                                            ? []
                                            : (accounts || [])
                                                  .filter((a) => !a.is_main_account)
                                                  .filter((a) => inAnyRange(toAccountNumber(a.number), sectionRanges))
                                                  .map((a) => ({ label: a.name, amount: accountsAmountByLabel.get(String(a.name)) || 0 }));
                                        const addedCategories = scenarioAddedCategories[group] || [];

                                        const seen = new Set();
                                        const allItems = []
                                            .concat(baseItems)
                                            .concat(savedAccounts)
                                            .concat(children)
                                            .concat(addedCategories.map((it) => ({ label: it.label, amount: 0 })))
                                            .filter((it) => {
                                                if (!it?.label) return false;
                                                if (seen.has(it.label)) return false;
                                                seen.add(it.label);
                                                return true;
                                            });

                                        const total = allItems.reduce((sum, it) => {
                                            const key = `${group}:${it.label}`;
                                            const state = scenarioOverrides[key];
                                            const enabled = state ? Boolean(state.enabled) : true;
                                            const value = Number.isFinite(Number(state?.value)) ? Number(state.value) : it.amount;
                                            return sum + (enabled ? value : it.amount);
                                        }, 0);
                                        sectionTotals.set(section.title, total);
                                    });

                                    const revenue = (sectionTotals.get('Income') || 0) + (sectionTotals.get('Other Income') || 0);
                                    const expenses =
                                        (sectionTotals.get('Cost of Goods Sold') || 0) +
                                        (sectionTotals.get('Advertising & Marketing') || 0) +
                                        (sectionTotals.get('Team') || 0) +
                                        (sectionTotals.get('Practice Costs') || 0) +
                                        (sectionTotals.get('Overheads') || 0) +
                                        (sectionTotals.get('Business Development') || 0) +
                                        (sectionTotals.get('Taxes') || 0) +
                                        (sectionTotals.get('Other Expenses') || 0);
                                    const profit = revenue - expenses;
                                    const margin = revenue ? (profit / revenue) * 100 : 0;

                                    return (
                                        <Box sx={{ px: '16px' }}>
                                            <Box
                                                sx={{
                                                    bgcolor: '#F6F7FF',
                                                    border: '1px solid #CAD2F9',
                                                    borderRadius: '10px',
                                                    p: '16px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '12px',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: '"Rethink Sans"',
                                                        fontWeight: 500,
                                                        fontSize: 14,
                                                        lineHeight: 1.43,
                                                        letterSpacing: '0.17px',
                                                        textTransform: 'uppercase',
                                                        color: '#536DEC',
                                                    }}
                                                >
                                                    Projected Performance
                                                </Typography>

                                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: '12px' }}>
                                                    <ScenarioMetric
                                                        label="Net Profit"
                                                        value={formatMoney(profit)}
                                                        valueColor={profit < 0 ? '#D32F2F' : '#2E7D32'}
                                                    />
                                                    <Divider orientation="vertical" flexItem />
                                                    <ScenarioMetric label="Profit Margin" value={`${margin.toFixed(1)}%`} />
                                                    <Divider orientation="vertical" flexItem />
                                                    <ScenarioMetric label="Revenue" value={formatMoney(revenue)} />
                                                    <Divider orientation="vertical" flexItem />
                                                    <ScenarioMetric label="Expenses" value={formatMoney(expenses)} />
                                                </Stack>
                                            </Box>
                                        </Box>
                                    );
                                })()}
                            </Box>

                            <Box sx={{ px: '16px', pb: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {sections.map((section) => {
                                    const group = section.group;
                                    if (!group) return null;
                                    const data = breakdowns[group];
                                    const accountsAmountByLabel = new Map(
                                        (data?.accounts || []).map((it) => [String(it.label), Number(it.amount) || 0])
                                    );
                                    const baseItems = (data?.items || []).map((it) => ({
                                        label: it.label,
                                        amount: accountsAmountByLabel.get(String(it.label)) || 0,
                                    }));
                                    const baseLabels = new Set(baseItems.map((it) => it.label));
                                    const savedAccounts = (data?.accounts || [])
                                        .filter((it) => it.amount && !baseLabels.has(it.label))
                                        .map((it) => ({ label: it.label, amount: Number(it.amount) || 0 }));
                                    const sectionRanges = section.ranges || [];
                                    const children = isEditMode
                                        ? []
                                        : (accounts || [])
                                              .filter((a) => !a.is_main_account)
                                              .filter((a) => inAnyRange(toAccountNumber(a.number), sectionRanges))
                                              .map((a) => ({ label: a.name, amount: accountsAmountByLabel.get(String(a.name)) || 0 }));
                                    const addedCategories = scenarioAddedCategories[group] || [];

                                    const seen = new Set();
                                    const allItems = []
                                        .concat(baseItems)
                                        .concat(savedAccounts)
                                        .concat(children)
                                        .concat(addedCategories.map((it) => ({ label: it.label, amount: 0 })))
                                        .filter((it) => {
                                            if (!it?.label) return false;
                                            if (seen.has(it.label)) return false;
                                            seen.add(it.label);
                                            return true;
                                        });

                                    const total = allItems.reduce((sum, it) => {
                                        const key = `${group}:${it.label}`;
                                        const state = scenarioOverrides[key];
                                        const enabled = state ? Boolean(state.enabled) : true;
                                        const value = Number.isFinite(Number(state?.value)) ? Number(state.value) : it.amount;
                                        return sum + (enabled ? value : it.amount);
                                    }, 0);

                                    return (
                                        <ScenarioSectionRow
                                            key={section.title}
                                            title={section.title}
                                            total={formatMoney(total)}
                                            expanded={scenarioExpandedSections.includes(section.title)}
                                            items={allItems}
                                            totalLabel={`Total ${section.title}`}
                                            onToggle={() => {
                                                setScenarioExpandedSections((prev) => {
                                                    const existing = Array.isArray(prev) ? prev : [];
                                                    if (existing.includes(section.title)) {
                                                        return existing.filter((t) => t !== section.title);
                                                    }
                                                    return existing.concat(section.title);
                                                });
                                            }}
                                            getItemState={(item) => {
                                                const key = `${group}:${item.label}`;
                                                return scenarioOverrides[key];
                                            }}
                                            setItemState={(item, next) => {
                                                const key = `${group}:${item.label}`;
                                                setScenarioOverrides((prev) => ({ ...prev, [key]: next }));
                                            }}
                                            onAddCategory={({ label, amount }) => {
                                                setScenarioAddedCategories((prev) => {
                                                    const existing = Array.isArray(prev[group]) ? prev[group] : [];
                                                    return { ...prev, [group]: existing.concat({ label, amount: 0 }) };
                                                });
                                                setScenarioOverrides((prev) => ({ ...prev, [`${group}:${label}`]: { enabled: true, value: amount } }));
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    </Card>
                </Box>
            </Box>
        </ClientDashboardLayout>
    );
}
